/**
 * RotateOverlay.ts — the "turn your phone sideways" nudge.
 *
 * Dribble Racer is landscape-first: the chase cam, the wide HUD and (after the
 * M-landscape pass) the menus are all built for a sideways phone. So when a
 * PHONE is held in PORTRAIT we cover the screen with a friendly prompt to
 * rotate. Nearly all the logic is CSS — the `@media (orientation: portrait) and
 * (pointer: coarse) …` rule in ui.css shows/hides this element automatically as
 * the device turns, so it appears on the menus, on a friend's race link, mid
 * race, everywhere, and vanishes the instant the phone goes landscape.
 *
 * This module only (a) builds the markup once and (b) wires the "Continue
 * anyway" escape hatch: some players have their OS rotation-lock on, so turning
 * the phone does nothing and a hard block would trap them. Tapping the link sets
 * a body class that hides the overlay for the rest of the session.
 */
import { el } from '@/ui/dom';
import { subscribeViewport } from '@/ui/viewport';

/** Rotation indicator drawn around the phone (a circular arrow). */
const ARROWS_SVG =
  '<svg class="rotate-arrows" viewBox="0 0 120 120" aria-hidden="true">' +
  '<path d="M97 40 A44 44 0 1 0 99 72" fill="none" stroke="currentColor" ' +
  'stroke-width="5" stroke-linecap="round"/>' +
  '<path d="M97 40 l-13 -1 l7 11 z" fill="currentColor"/>' +
  '</svg>';

/** The phone glyph that tips from portrait to landscape on a loop. */
const PHONE_SVG =
  '<svg class="rotate-phone" viewBox="0 0 100 100" aria-hidden="true">' +
  '<g class="rotate-phone-spin">' +
  '<rect class="rotate-phone-frame" x="34" y="15" width="32" height="70" rx="7"/>' +
  '<rect class="rotate-phone-screen" x="38" y="21" width="24" height="55" rx="2.5"/>' +
  '<rect class="rotate-phone-speaker" x="45" y="18" width="10" height="1.8" rx="0.9"/>' +
  '<circle class="rotate-phone-home" cx="50" cy="80" r="1.9"/>' +
  '</g>' +
  '</svg>';

/** Build the overlay once and mount it. Idempotent-ish: call a single time. */
export function mountRotateOverlay(parent: HTMLElement): void {
  const root = el('div', 'rotate-overlay', parent);
  root.innerHTML =
    '<div class="rotate-inner">' +
    `<div class="rotate-phone-wrap">${ARROWS_SVG}${PHONE_SVG}</div>` +
    '<div class="rotate-title">Rotate your phone</div>' +
    '<div class="rotate-sub">Dribble Racer plays best in landscape</div>' +
    '<button class="rotate-skip" type="button">Continue anyway</button>' +
    '</div>';

  root.querySelector('.rotate-skip')?.addEventListener('click', () => {
    document.body.classList.add('rotate-dismissed');
  });

  // M111c (TestFlight, real iPhones): a physical rotation could leave
  // WebKit's media-query state STALE inside the app shell — the prompt kept
  // painting (rotated, half-cropped) until the next tap forced a layout.
  // The M95 viewport machinery measures the truth through four independent
  // signals + a settle ramp, so it drives a hard kill class the CSS cannot
  // miss. CSS stays the primary show/hide; this only ever FORCES the
  // landscape-hidden state (and releases it back to CSS in portrait).
  subscribeViewport(() => {
    root.classList.toggle('rotate-js-landscape', window.innerWidth > window.innerHeight);
    root.classList.toggle('rotate-js-not-phone', !isPhoneSizedScreen());
  });
  // Stamp once up front too: the CSS query can match before the first
  // viewport signal lands, and a one-frame "Rotate your phone" flash on an
  // iPad is exactly the artifact this guard exists to remove.
  root.classList.toggle('rotate-js-not-phone', !isPhoneSizedScreen());
}

/**
 * M121 — is this a PHONE-sized device (not just a phone-sized WINDOW)?
 *
 * The CSS gate uses `max-width: 560px` in portrait, which is the right test
 * for a phone's short edge — but on an iPad in Split View / Slide Over the
 * app gets a ~320px-wide WINDOW on a 820-1024px SCREEN, so the query matches
 * and the overlay tells a tablet user to rotate a device that cannot help.
 *
 * `window.screen` reports the DISPLAY, not the window: it does not change
 * when an iPad multitasking window is resized, so its short edge is the one
 * reliable phone-vs-tablet signal available to the page. Phones report
 * <= 430 CSS px on the short edge (iPhone 16 Pro Max is 440 — still well
 * under the 560 the CSS already uses), tablets >= 744. The overlay is only
 * ever HIDDEN by this, and only on hardware the CSS gate was never meant to
 * cover, so phone behaviour is unchanged. If `screen` is unavailable for any
 * reason we fall back to "phone" and let the CSS decide, exactly as before.
 */
function isPhoneSizedScreen(): boolean {
  const s = window.screen as Screen | undefined;
  const w = s?.width;
  const h = s?.height;
  if (typeof w !== 'number' || typeof h !== 'number' || w <= 0 || h <= 0) return true;
  return Math.min(w, h) <= 560;
}
